export interface AriaResponse {
  markdown: string;
  followups?: string[];
}

/** Some saved mobile replies contain literal control characters inside JSON strings. */
function escapeStringControls(json: string): string {
  let quoted = false;
  let escaped = false;
  let result = '';
  for (const char of json) {
    if (quoted && !escaped && char.charCodeAt(0) < 0x20) {
      result += `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`;
      continue;
    }
    result += char;
    if (escaped) escaped = false;
    else if (quoted && char === '\\') escaped = true;
    else if (char === '"') quoted = !quoted;
  }
  return result;
}

/** Decode the response envelope before Markdown rendering, including fenced saved replies. */
export function tryParseAriaResponse(content: string): AriaResponse | null {
  const trimmed = content.trim();
  // Only unwrap a fence enclosing the entire answer; ordinary Markdown code samples stay intact.
  const fenced = /^```(?:json)?\s*\r?\n([\s\S]*?)\r?\n```\s*$/i.exec(trimmed);
  const json = fenced ? fenced[1].trim() : trimmed;
  if (!json.startsWith('{')) return null;

  for (const candidate of [json, escapeStringControls(json)]) {
    try {
      const parsed: unknown = JSON.parse(candidate);
      if (!parsed || typeof parsed !== 'object' || !('markdown' in parsed)
          || typeof parsed.markdown !== 'string') continue;
      return {
        markdown: parsed.markdown,
        followups: 'followups' in parsed && Array.isArray(parsed.followups)
          ? parsed.followups.filter((item): item is string => typeof item === 'string')
          : [],
      };
    } catch {
      // Leave incomplete streaming replies and ordinary Markdown to the existing renderer.
    }
  }
  return null;
}
